import React from 'react';
import { Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import FacebookMerchantCreativeCard from '../components/marketing/FacebookMerchantCreativeCard';
import {
  merchantLaunchConcepts,
} from '../content/facebookMerchantLaunchBundle';
import { seoConfig } from '../config/seo';

export const FacebookMerchantLaunchPackage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#fff8f1]" data-export-ready="true">
      <SEO
        title={`Facebook Merchant Launch Bundle - ${seoConfig.siteName}`}
        description="Final launch-ready Facebook and Instagram merchant-acquisition bundle for Coupling WNC."
        canonical={`${seoConfig.siteUrl}/preview/facebook-merchant-launch-package`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/preview/facebook-merchant-launch-package`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          {
            name: 'Facebook Merchant Launch Bundle',
            url: `${seoConfig.siteUrl}/preview/facebook-merchant-launch-package`,
          },
        ])}
      />
      <Header />

      <GradientHero
        title="Facebook Merchant Launch Bundle"
        description="Launch-ready package for four WNC merchant-acquisition concepts, with final copy, placement-specific creatives, paid destination URLs, and local export filenames."
        className="py-16"
      >
        <div className="mx-auto mt-8 max-w-4xl rounded-[1.85rem] border border-white/20 bg-white/10 px-6 py-5 text-left text-white/92 backdrop-blur-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/72">
            Bundle Scope
          </p>
          <p className="mt-3 text-base leading-relaxed">
            This package is designed for launch review, export, and Ads Manager handoff. Each concept includes a
            final `4:5` feed creative, a final `9:16` story creative, the paid destination URL, and the recommended
            audience / funnel role.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="/preview/facebook-merchant-launch-console"
              className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#7b4a31] transition hover:bg-[#fff3e5]"
            >
              Open launch console
            </a>
          </div>
        </div>
      </GradientHero>

      <section className="px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-[2rem] border border-[#e7d8c9] bg-white p-6 shadow-[0_26px_70px_rgba(77,44,25,0.10)] sm:p-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-primary">
                Campaign matrix
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
                Four launch-ready concepts with different jobs.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600">
                `wnc-merchants` and `simple-pricing` are the strongest cold launch anchors. `pos-mode` is the workflow-fit
                proof ad. `local-merchant-network` is fully launch-ready, but it should usually be deployed to narrower or warmer traffic.
              </p>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-2">
              {merchantLaunchConcepts.map((concept) => (
                <article
                  key={`matrix-${concept.slug}`}
                  className="rounded-[1.6rem] border border-[#ead9ca] bg-[#fffaf5] p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
                      {concept.angle}
                    </span>
                    <span className="text-sm font-medium text-neutral-500">
                      {concept.funnelStage}
                    </span>
                  </div>
                  <h3 className="mt-4 text-2xl font-semibold text-neutral-900">
                    {concept.formats[0].headline}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                    {concept.launchRecommendation}
                  </p>
                  <dl className="mt-4 grid gap-3 text-sm text-neutral-700">
                    <div>
                      <dt className="font-semibold text-neutral-500">Audience</dt>
                      <dd>{concept.audience}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-neutral-500">CTA</dt>
                      <dd>{concept.cta}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-neutral-500">Paid destination URL</dt>
                      <dd className="mt-1 break-all rounded-[1rem] bg-white px-3 py-2 font-mono text-[0.78rem] text-[#7b4a31]">
                        {concept.paidDestinationUrl}
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-20">
        <div className="mx-auto flex max-w-7xl flex-col gap-10">
          {merchantLaunchConcepts.map((concept) => (
            <article
              key={concept.slug}
              className="rounded-[2rem] border border-[#e7d8c9] bg-white p-6 shadow-[0_26px_70px_rgba(77,44,25,0.10)] sm:p-8"
            >
              <div className="max-w-4xl">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
                    {concept.angle}
                  </span>
                  <span className="rounded-full bg-[#f8f2eb] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-600">
                    {concept.objective}
                  </span>
                </div>
                <h2 className="mt-4 text-4xl font-semibold tracking-tight text-neutral-900">
                  {concept.formats[0].headline}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-neutral-600">
                  {concept.note}
                </p>
                <p className="mt-4 text-sm font-medium text-neutral-700">
                  <span className="font-semibold text-neutral-500">Launch note:</span>{' '}
                  {concept.launchRecommendation}
                </p>
              </div>

              <div className="mt-8 grid gap-6 xl:grid-cols-2">
                {concept.formats.map((format) => (
                  <FacebookMerchantCreativeCard
                    key={format.id}
                    concept={concept}
                    format={format}
                  />
                ))}
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="rounded-[1.6rem] border border-[#ead9ca] bg-[#fffaf5] p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-primary">
                    Primary text
                  </p>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {concept.formats.map((format) => (
                      <div key={`${format.id}-copy`}>
                        <p className="text-sm font-semibold text-neutral-900">
                          {format.label}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                          {format.primaryText}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-[1.6rem] border border-[#ead9ca] bg-[#fffaf5] p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-primary">
                    Launch mapping
                  </p>
                  <dl className="mt-4 grid gap-4 text-sm text-neutral-700">
                    <div>
                      <dt className="font-semibold text-neutral-500">Audience</dt>
                      <dd>{concept.audience}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-neutral-500">Funnel stage</dt>
                      <dd>{concept.funnelStage}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-neutral-500">Paid destination URL</dt>
                      <dd className="mt-1 break-all rounded-[1rem] bg-white px-3 py-2 font-mono text-[0.78rem] text-[#7b4a31]">
                        {concept.paidDestinationUrl}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-neutral-500">Landing page</dt>
                      <dd>
                        <a
                          href={concept.landingPath}
                          className="text-brand-primary hover:underline"
                        >
                          {concept.landingPath}
                        </a>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="px-4 pb-20">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-[#e7d8c9] bg-white p-6 shadow-[0_26px_70px_rgba(77,44,25,0.10)] sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-primary">
            QA and handoff
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <article className="rounded-[1.4rem] border border-[#ead9ca] bg-[#fffaf5] p-5">
              <h3 className="text-lg font-semibold text-neutral-900">
                Message continuity
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                Each concept maps directly to a matching landing page instead of routing all paid traffic to the homepage.
              </p>
            </article>
            <article className="rounded-[1.4rem] border border-[#ead9ca] bg-[#fffaf5] p-5">
              <h3 className="text-lg font-semibold text-neutral-900">
                Export-ready assets
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                PNG exports are generated locally from the browser-native creative cards so the final visuals stay inside the WNC design system.
              </p>
            </article>
            <article className="rounded-[1.4rem] border border-[#ead9ca] bg-[#fffaf5] p-5">
              <h3 className="text-lg font-semibold text-neutral-900">
                Mobile review
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                The QA pass focuses on local render sanity for mobile viewports and in-app browser-like conditions before Ads Manager handoff.
              </p>
            </article>
          </div>
        </div>
      </section>

      <Footer description="Final launch-ready Facebook and Instagram merchant-acquisition bundle for Coupling WNC." />
    </div>
  );
};

export default FacebookMerchantLaunchPackage;
